import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../api';
import PartnerNavbar from '../../../components/PartnerNavbar';
import { User, Mail, Phone, Building2, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import '../../CreateEvent.css'; // reuse cef- layout styles

export default function PartnerProfilePage() {
    const { user, loading: authLoading, isPartner, isAdmin, updateUserInContext } = useAuth();
    const navigate = useNavigate();
    const canAccess = isPartner || isAdmin;

    const [form, setForm] = useState({
        name: user?.name || '',
        phone: user?.phone || '',
        organisation: user?.organisation || ''
    });
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        setForm(p => ({ ...p, [e.target.name]: e.target.value }));
        setSuccess(false);
        setError('');
    };

    const handleSave = async () => {
        if (!form.name.trim()) { setError('Name is required'); return; }
        try {
            setSaving(true);
            setError('');
            await api.updateProfile(form.name, form.phone, form.organisation);
            setSuccess(true);
            // Update the user context name if the context supports it
            if (typeof updateUserInContext === 'function') {
                updateUserInContext({ name: form.name });
            }
        } catch (err) {
            setError(err.message || 'Failed to save profile.');
        } finally {
            setSaving(false);
        }
    };

    if (authLoading) return <div className="cef-loading">Loading...</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (!canAccess) return <Navigate to="/dashboard" replace />;

    return (
        <>
            <PartnerNavbar />
            <div className="cef-wrapper">
                <div className="cef-header">
                    <button className="cef-back-btn" type="button" onClick={() => navigate('/partner')}>
                        <ArrowLeft size={16} /><span>Back to Dashboard</span>
                    </button>
                    <div>
                        <h1 className="cef-page-title">My Profile</h1>
                        <p className="cef-page-subtitle">Manage your account information</p>
                    </div>
                </div>

                <div style={{ maxWidth: 640 }}>
                    {/* Avatar / summary card */}
                    <div className="cef-card" style={{ flexDirection: 'row', alignItems: 'center', gap: 24, marginBottom: 0 }}>
                        <div style={{
                            width: 72, height: 72, borderRadius: '50%',
                            background: 'linear-gradient(135deg, var(--primary) 0%, #fecd6d 100%)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontSize: '1.75rem', fontWeight: 800, flexShrink: 0
                        }}>
                            {(form.name || user?.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)' }}>{form.name || user?.name}</div>
                            <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: 2 }}>{user?.email}</div>
                            <div style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 8,
                                padding: '2px 10px', borderRadius: 20, background: 'rgba(222,65,176,0.1)',
                                color: 'var(--primary)', fontSize: '0.75rem', fontWeight: 700
                            }}>
                                {isAdmin ? 'Admin' : 'Partner'}
                            </div>
                        </div>
                    </div>

                    {/* Edit form */}
                    <div className="cef-card" style={{ marginTop: 20 }}>
                        <label className="cef-section-label"><User size={14} /> Account Details</label>

                        {success && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 10, background: 'rgba(16,185,129,0.1)', color: '#059669', fontWeight: 600, fontSize: '0.875rem' }}>
                                <CheckCircle size={16} /> Profile saved successfully!
                            </div>
                        )}
                        {error && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 10, background: 'rgba(220,38,38,0.08)', color: '#DC2626', fontWeight: 600, fontSize: '0.875rem' }}>
                                <AlertCircle size={16} /> {error}
                            </div>
                        )}

                        <div className="cef-field">
                            <label className="cef-field-label"><User size={12} /> Full Name *</label>
                            <input className="cef-input" name="name" value={form.name} onChange={handleChange} placeholder="Your full name" />
                        </div>

                        <div className="cef-field">
                            <label className="cef-field-label"><Mail size={12} /> Email Address</label>
                            <input
                                className="cef-input"
                                value={user?.email || ''}
                                readOnly
                                style={{ background: 'var(--bg-accent)', cursor: 'not-allowed', color: 'var(--text-muted)' }}
                            />
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Email cannot be changed. Contact admin to update.</span>
                        </div>

                        <div className="cef-row">
                            <div className="cef-field">
                                <label className="cef-field-label"><Phone size={12} /> Phone Number</label>
                                <input className="cef-input" name="phone" value={form.phone} onChange={handleChange} placeholder="+65 9123 4567" />
                            </div>
                            <div className="cef-field">
                                <label className="cef-field-label"><Building2 size={12} /> Organisation</label>
                                <input className="cef-input" name="organisation" value={form.organisation} onChange={handleChange} placeholder="e.g. ATAM Dance Academy" />
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="cef-actions" style={{ marginTop: 20 }}>
                        <Link to="/change-password" className="cef-btn-secondary" style={{ textDecoration: 'none' }}>
                            Change Password
                        </Link>
                        <button className="cef-btn-primary" type="button" onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving…' : '✔ Save Profile'}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}
